import { computed, shallowRef, type MaybeRefOrGetter, toValue, watch } from "vue";
import {
  resolveSQDCWorkflowParallelJoinTarget,
  type SQDCWorkflowDefinition,
  type SQDCWorkflowInstance,
} from "./domain";
import { useSQDCWorkflowDefinitionForId, useSQDCWorkflowInstanceForId } from "./useSQDCWorkflows";

export function useSQDCWorkflowRuntime(
  workflowInstanceId: MaybeRefOrGetter<string | undefined>,
  workflowStepId: MaybeRefOrGetter<string | undefined>,
  workflowDefinitionId?: MaybeRefOrGetter<string | undefined>,
  workflowDefinition?: MaybeRefOrGetter<SQDCWorkflowDefinition | undefined>,
) {
  const hasWorkflowContext = computed(
    () =>
      toValue(workflowInstanceId) != null ||
      toValue(workflowDefinitionId) != null ||
      toValue(workflowDefinition) != null,
  );
  const instanceId = computed(() => toValue(workflowInstanceId));
  const {
    instance: queriedInstance,
    loading: instancesLoading,
    refetch: refetchInstances,
  } = useSQDCWorkflowInstanceForId(instanceId);
  const lastInstance = shallowRef<SQDCWorkflowInstance>();
  watch(
    [queriedInstance, instancesLoading],
    ([current, loading]) => {
      if (current != null) lastInstance.value = current;
      else if (!loading) lastInstance.value = undefined;
    },
    { immediate: true },
  );
  const instance = computed(
    () =>
      queriedInstance.value ??
      (instancesLoading.value && lastInstance.value?.workflowInstanceId === instanceId.value
        ? lastInstance.value
        : undefined),
  );
  const definitionId = computed(
    () => instance.value?.definitionId ?? toValue(workflowDefinitionId),
  );
  const { definition: loadedDefinition, loading: definitionsLoading } =
    useSQDCWorkflowDefinitionForId(
      definitionId,
      computed(() => hasWorkflowContext.value && toValue(workflowDefinition) == null),
    );
  const queriedDefinition = computed(() => {
    if (!hasWorkflowContext.value) return undefined;
    return toValue(workflowDefinition) ?? loadedDefinition.value;
  });
  const lastDefinition = shallowRef<SQDCWorkflowDefinition>();
  watch(
    [queriedDefinition, definitionsLoading],
    ([current, loading]) => {
      if (current != null) lastDefinition.value = current;
      else if (!loading) lastDefinition.value = undefined;
    },
    { immediate: true },
  );
  const definition = computed(() => {
    return (
      queriedDefinition.value ??
      (definitionsLoading.value && lastDefinition.value?.id === definitionId.value
        ? lastDefinition.value
        : undefined)
    );
  });
  const step = computed(() => {
    const instanceStep = instance.value?.steps.find(
      (candidate) => candidate.workflowStepId === toValue(workflowStepId),
    );
    if (instanceStep != null) return instanceStep;
    if (toValue(workflowInstanceId) != null) return undefined;

    const startNode = definition.value?.nodes.find((node) => node.type === "START");
    const firstNodeId = definition.value?.connections.find(
      (connection) => connection.sourceNodeId === startNode?.id,
    )?.targetNodeId;
    const firstNode = definition.value?.nodes.find((node) => node.id === firstNodeId);
    if (firstNode == null) return undefined;
    return {
      workflowStepId: "",
      node: firstNode,
      createdAt: "",
    };
  });
  const transitions = computed(() => {
    const currentDefinition = definition.value;
    const currentStep = step.value;
    if (currentDefinition == null || currentStep == null) return [];
    const definitionNode = currentDefinition.nodes.find(({ id }) => id === currentStep.node.id);
    const parallelBranchId =
      "parallelBranchId" in currentStep ? currentStep.parallelBranchId : undefined;
    const outgoingTransitions = currentDefinition.connections
      .filter((connection) => connection.sourceNodeId === currentStep.node.id)
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map((connection) => {
        const directTargetNode = currentDefinition.nodes.find(
          (node) => node.id === connection.targetNodeId,
        );
        const completesParallelBoundary =
          parallelBranchId != null &&
          directTargetNode?.type === "PARALLEL" &&
          directTargetNode.id === definitionNode?.parentParallelNodeId;
        const readyTarget = completesParallelBoundary
          ? resolveSQDCWorkflowParallelJoinTarget(
              instance.value!,
              currentDefinition,
              parallelBranchId,
            )
          : undefined;
        const targetNode = readyTarget ?? directTargetNode;
        return {
          ...connection,
          targetNode,
          label: "",
          completesParallelBranch: completesParallelBoundary,
          waitsForParallelJoin: completesParallelBoundary && readyTarget == null,
        };
      });
    return outgoingTransitions;
  });

  return {
    instance,
    definition,
    step,
    transitions,
    loading: computed(() => instancesLoading.value || definitionsLoading.value),
    refetchInstances,
  };
}
